<template>
	<main v-if="pageDefinition && (toolDefinition || isStaticPlaceholderPage || isStandaloneDemoPage)" class="public-tool-demo-shell-root qwoach-form">
		<AccountSessionOverlay/>
		<section class="public-tool-demo-shell-root__header">
			<div class="public-tool-demo-shell-root__header-copy">
				<div class="public-tool-demo-shell-root__eyebrow">Qwoach tool demo</div>
				<div v-if="isDevOnlyTool" class="public-tool-demo-shell-root__eyebrow">{{ devOnlyLabel }}</div>
				<h1>{{ pageDefinition.h1 }}</h1>
				<p>{{ pageDefinition.intro }}</p>
				<div v-if="tagLabels.length" class="tool-demo-tags qw-row flex-wrap mt-3" role="group" aria-label="Tool tags">
					<span v-for="label in tagLabels" :key="label" class="tool-demo-tags__chip px-3 py-1">{{ label }}</span>
				</div>
			</div>

			<el-button
				type="primary"
				class="public-tool-demo-shell-root__cta"
				:disabled="isPrimaryCtaDisabled"
				@click="onPrimaryCtaClicked">
				<span>{{ ctaText }}</span>
			</el-button>
		</section>

		<section v-if="isStaticPlaceholderPage" class="public-tool-demo-shell-root__demo-placeholder">
			<h2>{{ pageDefinition.demoTitle }}</h2>
			<p>{{ pageDefinition.demoPrompt }}</p>
			<el-button type="primary" class="mt-3" @click="onPrimaryCtaClicked('placeholder')">Request access</el-button>
			<div ref="accessRequest" popover="auto" role="dialog" aria-labelledby="tool-access-title" aria-describedby="tool-access-description" class="tool-access-dialog p-4">
				<div class="tool-access-dialog__header mb-3">
					<h2 class="tool-access-dialog__title" id="tool-access-title">Request access</h2>
					<qw-button class="tool-access-dialog__close" aria-label="Close access request" @click="$refs.accessRequest.hidePopover()"><span aria-hidden="true">×</span></qw-button>
				</div>
				<p class="tool-access-dialog__description" id="tool-access-description">Request access to {{ toolTitle }}. Enter your email below.</p>
				<form
					class="tool-access-dialog__form mt-3"
					:action="waitlistEndpoint"
					method="post"
					@submit="onInterestFormSubmitted">
					<label class="tool-access-dialog__label" for="tool-access-email">Email address</label>
					<input id="tool-access-email" class="tool-access-dialog__email px-3 py-2" ref="waitlistEmailInput" type="email" name="email" aria-label="Email address" placeholder="you@example.com" autocomplete="email" :disabled="hasRequestedAccess" required>
					<input type="hidden" name="toolType" :value="pageDefinition.toolType">
					<input type="hidden" name="slug" :value="pageDefinition.slug">
					<input type="hidden" name="toolTitle" :value="toolTitle">
					<input type="hidden" name="source" value="publicDemo">
					<qw-button class="tool-access-dialog__submit w-100 px-3 py-2" native-type="submit" type="primary" :disabled="isWaitlistSubmitting || hasRequestedAccess">{{ waitlistButtonText }}</qw-button>
				</form>
				<p v-if="waitlistStatusMessage" class="tool-access-dialog__status" role="status" aria-live="polite">
					{{ waitlistStatusMessage }}
				</p>
			</div>
		</section>

		<section v-else class="public-tool-demo-shell-root__demo">
			<h2 class="public-tool-demo-shell-root__explore-heading">Explore this tool</h2>
			<component
				v-if="standaloneDemoComponent"
				ref="standaloneDemo"
				:is="standaloneDemoComponent"
				:page-definition="pageDefinition"
				@account-created="onPublicDemoAccountCreated"
				@tab-change="onStandaloneTabChanged"
				@save-completed="onStandaloneSaveCompleted"
				@save-failed="onStandaloneSaveFailed"
				@demo-ready="onStandaloneDemoReady"
			/>
			<el-tabs
				v-else
				v-model="activeTab"
				type="border-card"
				class="public-tool-demo-shell-root__tabs"
				@tab-click="onTabClicked"
				@tab-change="onTabChanged">
				<el-tab-pane
					:name="TabName.CLIENT"
					:label="workTabLabel">
					<div class="public-tool-demo-shell-root__tab-label px-3">
						{{ workTabDescription }}
					</div>
					<div v-if="!isDemoRuntimeLoaded" class="card p-3 mt-3 public-tool-demo-shell-root__placeholder">
						<QwEmpty hide-action-button hide-image>
							<template #title>Loading client view...</template>
						</QwEmpty>
						<div class="public-tool-demo-shell-root__placeholder-loader">
							<LoadingIndicator
								:is-loading="true"
								message="Loading..."
							/>
						</div>
					</div>

					<div v-else class="card p-3 mt-3 public-tool-demo-shell-root__renderer">
						<div v-if="blankPrintRendererComponent" class="text-right mb-3 hide-on-print">
							<qw-button @click="onPrintBlankClicked">
								<i class="fontello icon-print"></i> Print blank copy
							</qw-button>
						</div>
						<component
							v-if="workRendererComponent"
							ref="workRenderer"
							:is="workRendererComponent"
							v-model:engine-payload="enginePayload"
							:tool-instance="toolInstance"
							:engine-api="workEngineApi"
							:render-context="publicDemoRenderContext"
							@print-client-copy="onPrintClientCopyClicked"
						/>
					</div>
				</el-tab-pane>

				<el-tab-pane
					:name="TabName.RESULT"
					label="Result View">
					<div class="public-tool-demo-shell-root__tab-label px-3">
						{{ pageDefinition.resultTabDescription || 'See how the client’s responses appear in your review.' }}
					</div>
					<div v-if="!isDemoRuntimeLoaded" class="card p-3 mt-3 public-tool-demo-shell-root__placeholder">
						<QwEmpty hide-action-button hide-image>
							<template #title>Loading result review...</template>
						</QwEmpty>
						<div class="public-tool-demo-shell-root__placeholder-loader">
							<LoadingIndicator
								:is-loading="true"
								message="Loading..."
							/>
						</div>
					</div>

					<div v-else class="card p-3 mt-3 public-tool-demo-shell-root__renderer">
						<GenericToolResultSurface
							v-if="resultRendererComponent"
							v-model:tool-result="demoToolResult"
							:tool-type="toolDefinition.toolType"
							:tool-instance="toolInstance"
							:result-renderer-component="resultRendererComponent"
							:result-print-renderer-component="resultPrintRendererComponent"
							:engine-api="resultEngineApi"
							:render-context="practitionerReviewRenderContext"
							@create-revision="onDemoRevision"
						/>
					</div>
				</el-tab-pane>

				<el-tab-pane
					:name="TabName.SETTINGS"
					label="Practitioner Settings">
					<div class="public-tool-demo-shell-root__tab-label px-3">
						These are the settings you can customize
					</div>
					<div v-if="!isDemoRuntimeLoaded" class="card p-3 mt-3 public-tool-demo-shell-root__placeholder">
						<QwEmpty hide-action-button hide-image>
							<template #title>Loading settings...</template>
						</QwEmpty>
						<div class="public-tool-demo-shell-root__placeholder-loader">
							<LoadingIndicator
								:is-loading="true"
								message="Loading..."
							/>
						</div>
					</div>

					<div v-else class="card p-3 mt-3 public-tool-demo-shell-root__renderer">
						<component
							v-if="configRendererComponent"
							:is="configRendererComponent"
							v-model:tool-instance="toolInstance"
							:engine-api="configEngineApi"
							:render-context="publicDemoRenderContext"
							@change="onDemoTemplateChanged"
						/>
					</div>
				</el-tab-pane>
			</el-tabs>

		</section>

		<section v-if="hasSeoContent" class="public-tool-demo-shell-root__guide">
			<div class="public-tool-demo-shell-root__guide-header">
				<div class="public-tool-demo-shell-root__guide-eyebrow">Tool guide</div>
				<h2>Learn more about {{ toolTitle }}</h2>
				<p>
					Use this background section to understand when the tool fits, how practitioners use it,
					and what to consider before sharing it with clients.
				</p>
			</div>

			<section v-if="pageDefinition.sections?.length" class="public-tool-demo-shell-root__sections">
				<article
					v-for="section in pageDefinition.sections"
					:key="section.title"
					class="public-tool-demo-shell-root__section">
					<h2>{{ section.title }}</h2>
					<p>{{ section.body }}</p>
					<a
						v-if="section.sourceUrl"
						:href="section.sourceUrl"
						target="_blank"
						rel="noopener noreferrer">
						{{ section.sourceLabel || section.sourceUrl }}
					</a>
				</article>
			</section>

			<section v-if="pageDefinition.faqItems?.length" class="public-tool-demo-shell-root__faq-list">
				<article
					v-for="faqItem in pageDefinition.faqItems"
					:key="faqItem.question"
					class="public-tool-demo-shell-root__faq">
					<h3>{{ faqItem.question }}</h3>
					<p>{{ faqItem.answer }}</p>
					<a
						v-if="faqItem.sourceUrl"
						:href="faqItem.sourceUrl"
						target="_blank"
						rel="noopener noreferrer">
						{{ faqItem.sourceLabel || faqItem.sourceUrl }}
					</a>
				</article>
			</section>
		</section>

		<section v-if="relatedTools.length" class="public-tool-demo-shell-root__related">
			<div class="public-tool-demo-shell-root__related-heading">
				<h2>Related tools</h2>
				<p>Explore other ways to assess, plan, and work with clients.</p>
			</div>
			<ul class="public-tool-demo-shell-root__related-grid">
				<li v-for="tool in relatedTools" :key="tool.slug">
					<a class="public-tool-demo-shell-root__related-card" :href="'/tools/' + tool.slug">
						<h3>{{ tool.h1 }}</h3>
						<p>{{ tool.metaDescription }}</p>
						<span class="public-tool-demo-shell-root__related-action">Explore tool <span aria-hidden="true">→</span></span>
					</a>
				</li>
			</ul>
		</section>

		<div v-if="statusMessage" class="public-tool-demo-shell-root__status">
			{{ statusMessage }}
		</div>
	</main>
</template>

<script>
import AccountSessionOverlay from "@/core/components/AccountSessionOverlay.vue";
import "@/../public/tool-access-dialog.css";
import "@/../public/tool-demo-tags.css";
import "@/../public/tool-demo-related.css";
import {getToolDemoTagLabels, getRelatedToolDemoPages} from "@shared/GenericTools/ToolDemoDiscovery";
import {
	ElButton,
	ElTabPane,
	ElTabs,
} from "element-plus";
import BaseToolInstance from "@shared/GenericTools/BaseToolInstance.js";
import {createPersistableDemoConfig} from "@shared/GenericTools/ToolDemoConfig.js";
import ToolDefinitionCatalog from "@shared/GenericTools/ToolDefinitionCatalog.js";
import {DEV_ONLY_LABEL, isProductionToolRelease, isToolManuallyApproved} from "@shared/GenericTools/ToolReleaseStatus.js";
import {PlanEnum} from "@shared/Subscription/Plans/PlanEnum.js";
import {
	getToolDemoPageDefinitionByType,
} from "@shared/GenericTools/ToolDemoPageCatalog.js";
import QwEmpty from "@/core/components/Controls/QwEmpty.vue";
import LoadingIndicator from "@/core/components/LoadingIndicator.vue";
import GenericToolResultSurface from "@/core/modules/coach/Resources/CoachingTools/GenericTools/GenericToolResultSurface.vue";
import {
	PublicToolDemoAnalyticsEvent,
	buildPublicToolDemoAnalyticsProps,
	trackPublicToolDemoEvent,
} from "@/core/modules/coach/Resources/CoachingTools/GenericTools/PublicDemo/PublicToolDemoAnalyticsHelper.js";
import {
	getStandalonePublicDemoComponent,
} from "@/core/modules/coach/Resources/CoachingTools/GenericTools/PublicDemo/PublicToolDemoStandaloneRegistry.js";
import {createDialogLazy} from "@/utils/helpers-external.js";
import ExitIntentHelper from "@/core/js/helpers/ExitIntentHelper.js";

let CreateCoachAccountDialog = () => import("@/core/modules/Login/CreateCoachAccountDialog.vue")
let createCoachAccountDialog = createDialogLazy(CreateCoachAccountDialog)

const demoFeedbackDialog = createDialogLazy(() => import("@/tools/FeedbackGather/FeedbackDialog.vue"))

const TabName = Object.freeze({
	SETTINGS: "settings",
	CLIENT: "client",
	RESULT: "result",
})
const PUBLIC_DEMO_PENDING_SAVE_KEY = "qwPendingToolDemoSave"

export default {
	name: "PublicToolDemoShell",

	components: {
		AccountSessionOverlay,
		ElButton,
		ElTabs,
		ElTabPane,
		GenericToolResultSurface,
		QwEmpty,
		LoadingIndicator,
	},

	props: {
		bootPage: Object,
	},

	data() {
		return {
			TabName,
			exitIntentHelper: null,
			isDemoShellUnmounted: false,
			toolDefinitionCatalog: new ToolDefinitionCatalog(),
			activeTab: TabName.CLIENT,
			toolInstance: null,
			enginePayload: {},
			demoToolResult: null,
			demoPriorResults: [],
			configRendererComponent: null,
			workRendererComponent: null,
			resultRendererComponent: null,
			blankPrintRendererComponent: null,
			resultPrintRendererComponent: null,
			isDemoRuntimeLoaded: false,
			isDemoRuntimeLoading: false,
			isUpgrading: false,
			statusMessage: "",
			fullRuntimeResolver: null,
			publicDemoAnalyticsManager: null,
			publicDemoDataManager: null,
			isViewAnalyticsTracked: false,
			isRuntimeLoadedAnalyticsTracked: false,
			isWaitlistSubmitting: false,
			hasRequestedAccess: false,
			waitlistStatusMessage: "",
			isStandaloneDemoReady: false,
			publicDemoRenderContext: {
				surface: "publicDemo",
				isPublicDemo: true,
			},
		}
	},

	computed: {
		devOnlyLabel() {return DEV_ONLY_LABEL},
		isDevOnlyTool() {return !!this.pageDefinition && !isProductionToolRelease() && !isToolManuallyApproved(this.pageDefinition.toolType)},
		tagLabels() { return this.pageDefinition ? getToolDemoTagLabels(this.pageDefinition) : [] },
		relatedTools() { return this.pageDefinition ? getRelatedToolDemoPages(this.pageDefinition) : [] },
		bootPageComputed() {
			return this.bootPage || window.QW_TOOL_DEMO_PAGE || {}
		},

		toolDefinition() {
			if (this.pageDefinition?.isStaticPlaceholder) return null
			return this.toolDefinitionCatalog.getToolDefinitionByType(this.bootPageComputed?.toolType)
		},

		pageDefinition() {
			return getToolDemoPageDefinitionByType(this.bootPageComputed?.toolType)
		},

		isStaticPlaceholderPage() {
			return !!(this.pageDefinition?.isStaticPlaceholder && !this.toolDefinition)
		},

		standaloneDemoComponent() {
			return getStandalonePublicDemoComponent(this.pageDefinition?.demoImplementation)
		},

		isStandaloneDemoPage() {
			return !!this.standaloneDemoComponent
		},

		toolTitle() {
			return this.toolDefinition?.title || this.pageDefinition?.h1 || "this tool"
		},

		workTabLabel() {
			return this.pageDefinition?.workTabLabel || "Client view"
		},

		workTabDescription() {
			return this.pageDefinition?.workTabDescription || "This is how your clients will see it"
		},

		hasSeoContent() {
			return !!(this.pageDefinition?.sections?.length || this.pageDefinition?.faqItems?.length)
		},

		ctaText() {
			return this.isUpgrading ? "Preparing link..." : (this.pageDefinition?.ctaText || "Save and get shareable link")
		},

		isPrimaryCtaDisabled() {
			return this.isUpgrading || (this.isStandaloneDemoPage && !this.isStandaloneDemoReady)
		},

		loginHref() {
			let returnPath = `/tools/${this.bootPageComputed?.slug || ""}`
			return `/app/login-coach?redirect=${encodeURIComponent(returnPath)}`
		},

		waitlistEndpoint() {
			return "/api/public/waitinglist/tool"
		},

		waitlistButtonText() {
			return this.isWaitlistSubmitting ? "Sending your request…" : "Request access"
		},

		configEngineApi() {
			let result = null
			if (this.toolDefinition) {
				result = this.fullRuntimeResolver?.getToolConfigRendererEngineApi(this.toolDefinition.toolType) || null
			}
			return result
		},

		workEngineApi() {
			let result = null
			if (this.toolDefinition) {
				result = this.fullRuntimeResolver?.getToolWorkRendererEngineApi(this.toolDefinition.toolType) || null
			}
			return result
		},

		resultEngineApi() {
			let result = null
			if (this.toolDefinition) {
				result = this.fullRuntimeResolver?.getToolResultRendererEngineApi(this.toolDefinition.toolType) || null
			}
			return result
		},

		practitionerReviewRenderContext() {
			return {
				surface: "publicDemo",
				accessMode: "owner",
				priorResults: [...(this.toolDefinition?.publicDemoConfig?.priorResults || []), ...this.demoPriorResults],
			}
		},
	},

	watch: {
		enginePayload: {
			deep: true,
			handler() {
				this.syncDemoToolResult()
			},
		},
	},

	async mounted() {
		this.setupDemoToolInstance()
		document.dispatchEvent(new Event("prerender-start"))
		await this.$nextTick()
		await this.setupPublicDemoAnalytics()
		this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.VIEWED)
		if (this.isStandaloneDemoPage) {
			this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.RUNTIME_LOADED, {
				activeTab: this.activeTab,
				toolInstanceId: this.bootPageComputed?.toolType || "",
				toolResultId: this.bootPageComputed?.toolType || "",
			})
		} else if (!this.isStaticPlaceholderPage) {
			await this.loadDemoRuntime()
			await this.resumePendingSaveIfNeeded()
			this.setupExitFeedback()
		}
	},

	beforeUnmount() {
		this.isDemoShellUnmounted = true
		this.exitIntentHelper?.unregister()
	},

	methods: {
		setupExitFeedback() {
			if (this.isDemoShellUnmounted || this.isStaticPlaceholderPage || !this.isDemoRuntimeLoaded || !this.toolDefinition || this.exitIntentHelper) return
			this.exitIntentHelper = new ExitIntentHelper(this.onDemoExitIntent.bind(this))
		},

		async onDemoExitIntent() {
			if (this.isDemoShellUnmounted || this.isUpgrading || !this.exitIntentHelper?.isEnabled) return
			const openDialogs = document.querySelectorAll('[role="dialog"], dialog[open], [popover]:popover-open')
			if (Array.from(openDialogs).some(dialog => dialog.getClientRects().length > 0)) return
			this.exitIntentHelper.setEnabled(false)
			try {
				await demoFeedbackDialog({
					title: "What should change for this tool to be usable for you?",
					message: "Tell us what is missing or what should work differently.",
					defaultCheckboxValue: false,
					enableSupportShortcuts: false,
					feedbackSubject: `Tool feedback: ${this.toolDefinition.title}`,
					feedbackData: {
						source: "publicDemoExit",
						toolType: this.toolDefinition.toolType,
						slug: this.pageDefinition.slug,
					},
				})
			} catch (error) {
				console.error("[public-demo-feedback] " + JSON.stringify({toolType: this.toolDefinition.toolType, message: error?.message || String(error)}))
				if (!this.isDemoShellUnmounted) this.exitIntentHelper?.setEnabled(true)
			}
		},

		async setupPublicDemoAnalytics() {
			if (!this.publicDemoAnalyticsManager) {
				let delayedAnalyticsManager = this.$analyticManager
				let analyticsModule = await import("@/entry/setupAnalytic.js")
				this.publicDemoAnalyticsManager = analyticsModule.default
				if (typeof delayedAnalyticsManager?.flushEventsTo === "function") {
					delayedAnalyticsManager.flushEventsTo(this.publicDemoAnalyticsManager)
				}
				let pageViewProps = this.getPublicDemoAnalyticsProps({
					title: this.pageDefinition.title || this.pageDefinition.h1 || document.title,
				})
				let appEntryOptions = {pageViewProps}
				await this.publicDemoAnalyticsManager.trackAppEntry(appEntryOptions)
			}
		},

		async getPublicDemoDataManager() {
			if (!this.publicDemoDataManager) {
				let dataManagerModule = await import("@/entry/setupDataManager.js")
				this.publicDemoDataManager = dataManagerModule.default
			}
			return this.publicDemoDataManager
		},

		getPublicDemoAnalyticsProps(extraProps = {}) {
			return buildPublicToolDemoAnalyticsProps({
				pageDefinition: this.pageDefinition,
				analyticsManager: this.publicDemoAnalyticsManager,
				isStaticPlaceholder: this.isStaticPlaceholderPage,
				isImplementedTool: !!this.toolDefinition,
				extraProps,
			})
		},

		trackPublicDemoAnalyticsEvent(eventName, extraProps = {}) {
			if (eventName === PublicToolDemoAnalyticsEvent.VIEWED && this.isViewAnalyticsTracked) {
				return
			}
			if (eventName === PublicToolDemoAnalyticsEvent.RUNTIME_LOADED && this.isRuntimeLoadedAnalyticsTracked) {
				return
			}

			trackPublicToolDemoEvent(
				this.publicDemoAnalyticsManager,
				eventName,
				this.getPublicDemoAnalyticsProps(extraProps)
			)

			if (eventName === PublicToolDemoAnalyticsEvent.VIEWED) {
				this.isViewAnalyticsTracked = true
			}
			if (eventName === PublicToolDemoAnalyticsEvent.RUNTIME_LOADED) {
				this.isRuntimeLoadedAnalyticsTracked = true
			}
		},

		onPrimaryCtaClicked(location = "header") {
			this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.CTA_CLICKED, {
				ctaLocation: typeof location === "string" ? location : "header",
				ctaText: this.ctaText,
				ctaDestination: this.isStaticPlaceholderPage ? "waitlist" : "save",
				targetRoute: this.isStaticPlaceholderPage ? this.waitlistEndpoint : "",
			})
			if (this.isStaticPlaceholderPage) {
				this.$refs.accessRequest.showPopover()
				this.$refs.waitlistEmailInput?.focus?.()
				return
			}
			if (this.isStandaloneDemoPage) {
				this.onStandaloneSaveAndGetShareableLink()
				return
			}

			this.onSaveAndGetShareableLink()
		},

		async onInterestFormSubmitted(event) {
			event?.preventDefault?.()
			if (this.isWaitlistSubmitting || this.hasRequestedAccess) {
				return
			}
			let email = event?.target?.elements?.email?.value || ""
			let commonCtaProps = {
				ctaLocation: "accessDialog",
				ctaText: this.ctaText,
				ctaDestination: "waitlist",
				targetRoute: this.waitlistEndpoint,
				emailProvided: !!email,
			}
			this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.CTA_CLICKED, {
				...commonCtaProps,
			})
			this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.INTEREST_SUBMITTED, {
				...commonCtaProps,
			})
			this.isWaitlistSubmitting = true
			this.waitlistStatusMessage = ""
			try {
				let dataManager = await this.getPublicDemoDataManager()
				let payload = this.getPublicDemoAnalyticsProps({
					email,
					pagePath: window.location?.pathname || "",
				})
				let result = await dataManager.apiPostToolWaitlistJoin(payload)
				if (result?.success !== true) throw new Error("Request not confirmed")
				this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.WAITLIST_JOINED, {
					...commonCtaProps,
				})
				this.hasRequestedAccess = true
				this.waitlistStatusMessage = `Request received. We'll email you about access.`
			} catch (error) {
				console.error("tool waitlist join failed", error)
				this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.WAITLIST_FAILED, {
					...commonCtaProps,
				})
				this.waitlistStatusMessage = "We couldn't confirm your request. Please try again."
			} finally {
				this.isWaitlistSubmitting = false
			}
		},

		async onTabClicked() {
			await this.loadDemoRuntime()
		},

		async onTabChanged(tabName) {
			let previousTab = this.activeTab
			this.activeTab = tabName
			if (previousTab !== tabName) {
				this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.TAB_CHANGED, {
					activeTab: tabName,
					previousTab,
					toolInstanceId: this.toolInstance?.id || "",
					toolResultId: this.demoToolResult?.id || "",
				})
			}
			await this.loadDemoRuntime()
		},

		setupDemoToolInstance() {
			if (this.toolDefinition && !this.isStandaloneDemoPage) {
				this.demoPriorResults = []
				this.activeTab = this.pageDefinition?.initialTab || TabName.CLIENT
				this.toolInstance = new BaseToolInstance({
					id: "public-demo",
					toolType: this.toolDefinition.toolType,
					ownerId: "public-demo",
					title: this.toolDefinition.title,
					config: this.cloneDemoData(this.toolDefinition.publicDemoConfig?.config || this.toolDefinition.defaultConfig),
				})
				this.enginePayload = this.cloneDemoData(this.toolDefinition.publicDemoConfig?.demoEnginePayload || {})
				this.syncDemoToolResult()
			}
		},

		async loadDemoRuntime() {
			if (!this.isStaticPlaceholderPage && !this.isStandaloneDemoPage && !this.isDemoRuntimeLoaded && !this.isDemoRuntimeLoading) {
				this.isDemoRuntimeLoading = true
				this.statusMessage = "Loading demo..."
				try {
					await this.loadFullRuntime()
					let resolver = await import("@/core/modules/coach/Resources/CoachingTools/GenericTools/GenericToolComponentResolver.js")
					this.fullRuntimeResolver = resolver
					this.configRendererComponent = resolver.getToolConfigRendererComponent(this.toolDefinition.toolType)
					this.workRendererComponent = resolver.getToolWorkRendererComponent(this.toolDefinition.toolType)
					this.resultRendererComponent = resolver.getToolResultRendererComponent(this.toolDefinition.toolType)
					this.blankPrintRendererComponent = resolver.getToolBlankPrintRendererComponent(this.toolDefinition.toolType)
					this.resultPrintRendererComponent = resolver.getToolResultPrintRendererComponent(this.toolDefinition.toolType)
					this.isDemoRuntimeLoaded = true
					this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.RUNTIME_LOADED, {
						activeTab: this.activeTab,
						toolInstanceId: this.toolInstance?.id || "",
						toolResultId: this.demoToolResult?.id || "",
					})
				} finally {
					this.statusMessage = ""
					this.isDemoRuntimeLoading = false
				}
			}
		},

		onDemoTemplateChanged() {
			this.toolInstance = new BaseToolInstance(this.cloneDemoData(this.toolInstance))
			this.syncDemoToolResult()
		},

		onPrintBlankClicked() {
			this.openPrintPage({
				mode: "blank",
				toolType: this.toolDefinition.toolType,
				toolInstance: this.toolInstance,
			})
		},

		openPrintPage(data) {
			let printKey = "p" + Date.now()
			localStorage.setItem(printKey, JSON.stringify(data))
			window.open(`/print/tools?lsKey=${encodeURIComponent(printKey)}`, "_blank")
		},
		onPrintClientCopyClicked(enginePayload) {
			this.openPrintPage({mode: "result", clientCopy: true, toolType: this.toolDefinition.toolType, toolInstance: this.toolInstance, toolResult: {enginePayload}})
		},
		onDemoRevision(enginePayload) {
			if (this.demoToolResult) this.demoPriorResults.push({...this.cloneDemoData(this.demoToolResult), id: `public-demo-revision-${this.demoPriorResults.length + 1}`, createdAt: new Date().toISOString()})
			this.enginePayload = this.cloneDemoData(enginePayload)
			this.syncDemoToolResult()
			this.activeTab = TabName.RESULT
		},

		syncDemoToolResult() {
			const previousPayload = this.demoToolResult?.enginePayload || {}
			const nextPayload = this.cloneDemoData(this.enginePayload || {})
			if (nextPayload.tracker && this.toolInstance?.config?.trackerKind === nextPayload.tracker.trackerKind) {
				nextPayload.tracker = this.cloneDemoData(this.toolInstance.config)
			}
			if (nextPayload.timeline && this.toolInstance?.config) {
				nextPayload.timeline.config = this.cloneDemoData(this.toolInstance.config)
			}
			if (nextPayload.itemList && this.toolInstance?.config?.ratingProfile) {
				nextPayload.itemList = this.cloneDemoData(this.toolInstance.config)
			}
			if (nextPayload.form && this.toolInstance?.config?.fields) {
				const currentForm = this.cloneDemoData(this.toolInstance.config)
				const currentIds = new Set((currentForm.fields || []).map(field => field.id))
				const previousFields = [...(previousPayload.form?.fields || []), ...(previousPayload.form?.archivedFields || [])]
				currentForm.archivedFields = previousFields.filter(field => !currentIds.has(field.id)).map(field => ({...field, archived: true}))
				const currentSectionIds = new Set((currentForm.portraitSections || []).map(section => section.id))
				const previousSections = [...(previousPayload.form?.portraitSections || []), ...(previousPayload.form?.archivedSections || [])]
				currentForm.archivedSections = previousSections.filter(section => !currentSectionIds.has(section.id)).map(section => ({...section, archived: true}))
				if (currentForm.subscaleProfile) {
					const currentItemIds = new Set((currentForm.subscaleProfile.items || []).map(item => item.id))
					const previousItems = [...(previousPayload.form?.subscaleProfile?.items || []), ...(previousPayload.form?.subscaleProfile?.archivedItems || [])]
					currentForm.subscaleProfile.archivedItems = previousItems.filter(item => !currentItemIds.has(item.id)).map(item => ({...item, archived: true}))
				}
				nextPayload.form = currentForm
			}
			if (previousPayload.privatePractitioner) {
				nextPayload.privatePractitioner = this.cloneDemoData(previousPayload.privatePractitioner)
			}
			if (previousPayload.sharedInterpretation) {
				nextPayload.sharedInterpretation = this.cloneDemoData(previousPayload.sharedInterpretation)
			}
			this.demoToolResult = {
				id: "public-demo-result",
				toolType: this.toolDefinition?.toolType || "",
				toolInstanceId: this.toolInstance?.id || "",
				ownerId: "public-demo",
				clientId: "public-demo-client",
				clientName: "Alex Morgan",
				enginePayload: nextPayload,
				summary: this.cloneDemoData({
					headline: this.toolDefinition?.title || "",
					toolTitle: this.toolDefinition?.title || "",
					...nextPayload,
				}),
			}
		},

		onStandaloneTabChanged(payload = {}) {
			this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.TAB_CHANGED, {
				activeTab: payload.activeTab || "",
				previousTab: payload.previousTab || "",
				toolInstanceId: payload.toolInstanceId || this.bootPageComputed?.toolType || "",
				toolResultId: payload.toolResultId || this.bootPageComputed?.toolType || "",
			})
		},

		onStandaloneDemoReady() {
			this.isStandaloneDemoReady = true
		},

		async onStandaloneSaveAndGetShareableLink() {
			if (this.isUpgrading || !this.isStandaloneDemoReady) {
				return
			}

			this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.SAVE_CLICKED, {
				ctaLocation: typeof location === "string" ? location : "header",
				ctaText: this.ctaText,
				ctaDestination: "save",
				toolInstanceId: this.bootPageComputed?.toolType || "",
				toolResultId: this.bootPageComputed?.toolType || "",
			})
			this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.SIGNUP_STARTED, {
				ctaLocation: typeof location === "string" ? location : "header",
				ctaText: this.ctaText,
				ctaDestination: "signup",
				toolInstanceId: this.bootPageComputed?.toolType || "",
				toolResultId: this.bootPageComputed?.toolType || "",
			})
			this.isUpgrading = true
			this.statusMessage = "Saving your tool..."
			try {
				let standaloneDemo = this.$refs.standaloneDemo
				if (typeof standaloneDemo?.saveDemo !== "function") {
					throw new Error("Standalone demo save handler is not available")
				}
				await standaloneDemo.saveDemo()
			} catch (error) {
				console.error("standalone public tool demo save failed", error)
			} finally {
				this.isUpgrading = false
				this.statusMessage = ""
			}
		},

		onStandaloneSaveCompleted(payload = {}) {
			this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.SAVE_COMPLETED, {
				ctaLocation: typeof location === "string" ? location : "header",
				ctaText: this.ctaText,
				ctaDestination: "ownedTool",
				toolInstanceId: payload.toolInstanceId || this.bootPageComputed?.toolType || "",
				toolResultId: payload.toolResultId || this.bootPageComputed?.toolType || "",
			})
		},

		onStandaloneSaveFailed(payload = {}) {
			this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.SAVE_FAILED, {
				ctaLocation: typeof location === "string" ? location : "header",
				ctaText: this.ctaText,
				ctaDestination: "signup",
				toolInstanceId: payload.toolInstanceId || this.bootPageComputed?.toolType || "",
				toolResultId: payload.toolResultId || this.bootPageComputed?.toolType || "",
			})
		},

		async onSaveAndGetShareableLink() {
			if (this.isUpgrading) {
				return
			}

			this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.SAVE_CLICKED, {
				ctaLocation: typeof location === "string" ? location : "header",
				ctaText: this.ctaText,
				ctaDestination: "save",
				toolInstanceId: this.toolInstance?.id || "",
				toolResultId: this.demoToolResult?.id || "",
			})
			this.isUpgrading = true
			this.statusMessage = "Preparing your tool..."
			try {
				this.storePendingSave()
				await this.loadDemoRuntime()
				let userProfile = await this.createPublicDemoAccountIfNeeded()
				if (!userProfile && !this.isLoggedIn) {
					return
				}
				this.statusMessage = "Saving your tool..."
				let fullRuntime = await this.loadFullRuntime()
				let dataManager = fullRuntime.Vue?.config?.globalProperties?.$dataManager
				let toolInstance = await dataManager.apiPostToolPublicDemoSave({
					toolType: this.toolDefinition.toolType,
					config: this.createPersistableDemoConfig(),
				})
				this.clearPendingSave()
				this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.SAVE_COMPLETED, {
					ctaLocation: typeof location === "string" ? location : "header",
					ctaText: this.ctaText,
					ctaDestination: "ownedTool",
					toolInstanceId: toolInstance?.id || "",
					toolResultId: this.demoToolResult?.id || "",
				})
				let route = fullRuntime.routerManager.router.resolve({
					name: "GenericToolTemplateEditRoute",
					params: {
						toolType: toolInstance.toolType,
						toolInstanceId: toolInstance.id,
					},
				})
				window.location.href = route.href
			} catch (error) {
				console.error("public tool demo save failed", error)
				this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.SAVE_FAILED, {
					ctaLocation: typeof location === "string" ? location : "header",
					ctaText: this.ctaText,
					ctaDestination: "save",
					toolInstanceId: this.toolInstance?.id || "",
					toolResultId: this.demoToolResult?.id || "",
				})
			} finally {
				this.isUpgrading = false
				this.statusMessage = ""
			}
		},

		async createPublicDemoAccountIfNeeded() {
			let userProfile = this.userProfile || null
			if (!this.isLoggedIn) {
				this.trackPublicDemoAnalyticsEvent(PublicToolDemoAnalyticsEvent.SIGNUP_STARTED, {
					ctaLocation: typeof location === "string" ? location : "header",
					ctaText: this.ctaText,
					ctaDestination: "signup",
					toolInstanceId: this.toolInstance?.id || "",
					toolResultId: this.demoToolResult?.id || "",
				})
				let toolType = this.toolDefinition?.toolType || ""
				let toolSlug = this.toolDefinition?.slug || ""
				userProfile = await createCoachAccountDialog({
					planId: PlanEnum.COMPLETE_PRACTICE_TOOLKIT,
					productName: "The Complete Practice Toolkit by Qwoach",
					onAccountCreated: this.onPublicDemoAccountCreated,
					accountCreatedAnalytics: {
						name: PublicToolDemoAnalyticsEvent.ACCOUNT_CREATED,
						props: this.getPublicDemoAnalyticsProps(),
					},
					canShowStudentEnrollDialog: false,
					userExtraProps: {
						firstTouch: `public_tool_demo_${toolType}`,
						account: "public-tool-demo",
						toolType,
						toolSlug,
					},
				})
				if (userProfile) {
					userProfile = await this.$authManager.fetchAndSyncUserProfile()
				}
			}
			return userProfile
		},

		async onPublicDemoAccountCreated() {
			try {
				let dataManager = await this.getPublicDemoDataManager()
				await dataManager.apiPostToolPublicDemoAccountCreatedNotify({
					toolType: this.pageDefinition.toolType,
				})
			} catch (error) {
				console.error("public tool demo signup notification failed", error)
			}
		},

		async resumePendingSaveIfNeeded() {
			let pendingSave = this.getPendingSave()
			if (pendingSave?.toolType === this.toolDefinition?.toolType) {
				this.toolInstance.config = pendingSave.config || this.toolInstance.config
				if (!this.isLoggedIn) {
					return
				}
				await this.onSaveAndGetShareableLink()
			}
		},

		storePendingSave() {
			window.sessionStorage?.setItem(PUBLIC_DEMO_PENDING_SAVE_KEY, JSON.stringify({
				toolType: this.toolDefinition?.toolType || "",
				config: this.createPersistableDemoConfig(),
			}))
		},

		createPersistableDemoConfig() {
			return createPersistableDemoConfig(this.toolInstance?.config || {}, this.toolDefinition?.publicDemoConfig?.config || {}, this.toolDefinition?.defaultConfig || {}, this.toolDefinition?.publicDemoConfig?.demoOnlyConfigPaths || [])
		},

		getPendingSave() {
			let result = null
			try {
				result = JSON.parse(window.sessionStorage?.getItem(PUBLIC_DEMO_PENDING_SAVE_KEY) || "null")
			} catch (error) {
				result = null
			}
			return result
		},

		clearPendingSave() {
			window.sessionStorage?.removeItem(PUBLIC_DEMO_PENDING_SAVE_KEY)
		},

		async loadFullRuntime() {
			return await import("@/entry/importFull.js")
		},

		cloneDemoData(value) {
			return JSON.parse(JSON.stringify(value || null))
		},
	},
}
</script>

<style lang="scss">
.public-tool-demo-shell-root {
	max-width: 1100px;
	margin: 0 auto;
	padding: 32px 16px 56px;
	color: #29323a;
	font-family: Montserrat, sans-serif;
	font-weight: 300;

	&__header {
		display: flex;
		gap: 24px;
		align-items: flex-start;
		justify-content: space-between;
		margin-bottom: 20px;
	}

	&__header-copy {
		max-width: 760px;

		h1 {
			margin: 0 0 12px;
			font-size: 2.3rem;
			font-weight: 700;
			letter-spacing: 0;
			line-height: 1.12;
		}

		p {
			margin: 0;
			color: #606266;
			font-size: 1.05rem;
			line-height: 1.55;
		}
	}

	&__eyebrow {
		margin-bottom: 8px;
		color: $color-primary-strong;
		font-size: 0.78rem;
		font-weight: 700;
		letter-spacing: 0;
		text-transform: uppercase;
	}

	&__section,
	&__faq {
		border: 1px solid #e4e7ed;
		border-radius: 8px;
		background: #fff;
	}

	&__demo {
		margin-bottom: 36px;
	}

	&__explore-heading {
		margin: 0 0 12px;
		color: #29323a;
		font-size: 1.5rem;
		font-weight: 700;
		line-height: 1.3;
	}

	&__demo-placeholder {
		border: 1px solid #e4e7ed;
		border-radius: 8px;
		background: #fff;
		margin-bottom: 36px;
		padding: 20px;

		h2 {
			margin: 0 0 8px;
			font-size: 1.35rem;
			font-weight: 700;
			letter-spacing: 0;
		}

		p {
			margin: 0;
			color: #606266;
			line-height: 1.5;
		}
	}

	&__access-panel {
		width: min(32rem, calc(100vw - 2rem));
		max-height: calc(100dvh - 2rem);
		overflow: auto;
		border: 1px solid #dcdfe6;
		border-radius: 8px;
		background: #fff;

		&::backdrop { background: rgba(0, 0, 0, 0.35); }
	}

	&__interest-form {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		margin-top: 16px;

		input {
			flex: 1 1 260px;
			min-height: 40px;
			border: 1px solid #dcdfe6;
			border-radius: 4px;
			padding: 8px 10px;
			color: #303133;
			background: #fff;
		}

		button {
			min-height: 40px;
			border: 1px solid #409eff;
			border-radius: 4px;
			padding: 8px 14px;
			background: #409eff;
			color: #fff;
			font-weight: 600;
		}
	}

	&__waitlist-status {
		margin-top: 10px;
		color: #606266;
		line-height: 1.5;
	}

	&__placeholder {
		min-height: 10rem;
		text-align: center;

		.el-empty {
			padding: 1.25rem 0 0.25rem;
		}
	}

	&__placeholder-loader {
		position: relative;
		min-height: 3rem;
		margin-top: -0.5rem;
	}

	&__renderer {
		padding-top: 16px;
	}

	&__tab-label {
		padding-top: 16px;
		color: #606266;
		font-size: 0.95rem;
		font-weight: 500;
		line-height: 1.45;
	}

	&__tab-label + &__placeholder,
	&__tab-label + &__renderer {
		padding-top: 10px;
	}

	&__sections,
	&__faq-list {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 16px;
		margin-top: 18px;
	}

	&__faq-list {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	&__guide {
		border-top: 1px solid #dcdfe6;
		margin-top: 44px;
		padding-top: 32px;
	}

	&__guide-header {
		max-width: 760px;

		h2 {
			margin: 0 0 10px;
			font-size: 1.65rem;
			font-weight: 700;
			letter-spacing: 0;
			line-height: 1.2;
		}

		p {
			margin: 0;
			color: #606266;
			line-height: 1.55;
		}
	}

	&__guide-eyebrow {
		margin-bottom: 8px;
		color: #909399;
		font-size: 0.78rem;
		font-weight: 700;
		letter-spacing: 0.04em;
		text-transform: uppercase;
	}

	&__section,
	&__faq {
		padding: 16px;

		h2,
		h3 {
			margin: 0 0 8px;
			font-size: 1.05rem;
			font-weight: 700;
			letter-spacing: 0;
		}

		p {
			margin: 0;
			color: #606266;
			line-height: 1.5;
		}
	}

	&__status {
		margin-top: 14px;
		color: #606266;
	}

	&__cta {
		flex: 0 0 auto;
		margin-top: 24px;
	}

	.el-button {
		align-items: center;
		appearance: none;
		background-color: #fff;
		border: 1px solid #dcdfe6;
		border-radius: 4px;
		box-sizing: border-box;
		color: #606266;
		cursor: pointer;
		display: inline-flex;
		font-family: inherit;
		font-size: 1rem;
		font-weight: 500;
		height: 32px;
		justify-content: center;
		line-height: 1;
		outline: none;
		padding: 8px 15px;
		text-align: center;
		transition: 0.1s;
		user-select: none;
		vertical-align: middle;
		white-space: nowrap;

		&:hover {
			background-color: #ecf5ff;
			border-color: #c6e2ff;
			color: #409eff;
		}

		&:active {
			background-color: #ecf5ff;
			border-color: #409eff;
			color: #409eff;
		}

		&--primary {
			background-color: #409eff;
			border-color: #409eff;
			color: #fff;
		}

		&--primary:hover {
			background-color: #79bbff;
			border-color: #79bbff;
			color: #fff;
		}

		&--primary:active {
			background-color: #337ecc;
			border-color: #337ecc;
			color: #fff;
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.66;
		}

		> span {
			align-items: center;
			display: inline-flex;
		}
	}

	&__tabs.el-tabs--border-card {
		border-color: #d6dee5;
	}

	&__tabs > .el-tabs__header {
		margin: 0;
		background: #f1f5f9;
		border-bottom-color: #d6dee5;

		.el-tabs__nav-wrap,
		.el-tabs__nav-scroll,
		.el-tabs__nav {
			position: relative;
		}

		.el-tabs__nav-scroll {
			overflow: hidden;
		}

		.el-tabs__nav {
			display: flex;
			float: left;
		}

		.el-tabs__nav-wrap::after {
			content: none;
		}

		.el-tabs__nav .el-tabs__item.is-top {
			color: #3c484f;
			font-family: inherit;
			font-size: 1.2rem;
			font-weight: 600;
			height: 56px;
			line-height: 56px;
			padding: 0 26px;
			white-space: nowrap;
		}

		.el-tabs__nav .el-tabs__item.is-top:hover,
		.el-tabs__nav .el-tabs__item.is-top.is-active {
			color: $color-primary-strong;
		}

		.el-tabs__nav .el-tabs__item.is-top.is-active {
			font-weight: 700;
			background: #fff;

			&::after {
				content: "";
				position: absolute;
				bottom: 0;
				left: 26px;
				right: 26px;
				height: 3px;
				background: $color-primary-strong;
			}
		}

		.el-tabs__active-bar {
			display: none;
		}

		.el-tabs__nav-prev,
		.el-tabs__nav-next {
			line-height: 56px;
		}
	}

	&__tabs.el-tabs--border-card > .el-tabs__content {
		overflow: visible;
		padding: 0;

		.public-tool-demo-shell-root__renderer,
		.public-tool-demo-shell-root__placeholder {
			border: 0;
			box-shadow: none;
		}
	}
}

@media (max-width: 820px) {
	.public-tool-demo-shell-root {
		&__header {
			display: block;
		}

		&__cta {
			margin-top: 12px;
			width: 100%;
		}

		&__sections,
		&__faq-list {
			grid-template-columns: 1fr;
		}

		&__tabs > .el-tabs__header .el-tabs__nav .el-tabs__item.is-top {
			font-size: 1.06rem;
			height: 52px;
			line-height: 52px;
			padding: 0 18px;
		}

		&__tabs > .el-tabs__header .el-tabs__nav .el-tabs__item.is-top.is-active::after {
			left: 18px;
			right: 18px;
		}

		&__tabs > .el-tabs__header .el-tabs__nav-prev,
		&__tabs > .el-tabs__header .el-tabs__nav-next {
			line-height: 52px;
		}
	}
}
</style>
